import { useWallet as useAptosWallet } from "@manahippo/aptos-wallet-adapter";
import { mplCandyMachine } from "@metaplex-foundation/mpl-candy-machine";
import { mplTokenMetadata } from "@metaplex-foundation/mpl-token-metadata";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import { walletAdapterIdentity } from "@metaplex-foundation/umi-signer-wallet-adapters";
import {
  useConnection as useSolanaConnection,
  useWallet as useSolanaWallet,
} from "@solana/wallet-adapter-react";
import { useWeb3React } from "@web3-react/core";
import { useMemo } from "react";

import { DEFAULT_EVM_CHAIN_ID, supportedChainsMap } from "../../config";
import { useCosmJsProvider } from "../../contexts";
import {
  AptosNetwork,
  CosmosNetwork,
  EvmNetwork,
  SolanaNetwork,
} from "../../interfaces";
import { isTestnetChain } from "../../utils/utils";

export const useNetworks = (): {
  networks: {
    evm: EvmNetwork;
    cosmos: CosmosNetwork;
    aptos: AptosNetwork;
    solana: SolanaNetwork;
  };
} => {
  // EVM
  const {
    chainId: evmChainId = Number(DEFAULT_EVM_CHAIN_ID),
    hooks: { usePriorityAccount },
    provider: evmProvider,
  } = useWeb3React();
  const evmAddress = usePriorityAccount();

  // Cosmos
  const {
    chainId: cosmJsChainId = "osmosis-1",
    address: cosmJsWalletAddress,
    provider: cosmJsProvider,
    connector: cosmJsConnector,
  } = useCosmJsProvider();

  // Aptos
  const { account: aptosAccount, network: aptosNetwork } = useAptosWallet();

  // Solana
  const solanaWallet = useSolanaWallet();
  const { connection: solanaConnection } = useSolanaConnection();

  // EVM
  const evm = useMemo(
    () => ({
      networkName: "EVM",
      chainId: evmChainId.toString(),
      address: evmAddress,
      provider: evmProvider,
      signer: evmProvider?.getSigner(),
      isTestnet: isTestnetChain(evmChainId.toString()),
    }),
    [evmChainId, evmAddress, evmProvider],
  );

  // Cosmos
  const cosmos = useMemo(
    () => ({
      networkName: "Cosmos",
      chainId: cosmJsChainId,
      address: cosmJsWalletAddress,
      provider: cosmJsProvider,
      connector: cosmJsConnector,
      isTestnet: isTestnetChain(cosmJsChainId),
    }),
    [cosmJsChainId, cosmJsWalletAddress, cosmJsProvider, cosmJsConnector],
  );

  // Aptos
  const aptos = useMemo(
    () => ({
      networkName: "Aptos",
      chainId: aptosNetwork?.chainId || "aptos",
      account: aptosAccount,
      address: aptosAccount?.address?.toString(),
      network: aptosNetwork,
      isTestnet: false,
    }),
    [aptosAccount, aptosNetwork],
  );

  // Solana
  const solana = useMemo(() => {
    const clusters = Object.values(supportedChainsMap.solana);
    const clusterName =
      clusters.find(
        (cluster) => cluster.rpcUrl === solanaConnection.rpcEndpoint,
      )?.chainId || "mainnet-beta";

    const umi = createUmi(solanaConnection)
      .use(walletAdapterIdentity(solanaWallet))
      .use(mplCandyMachine())
      .use(mplTokenMetadata());

    return {
      networkName: "Solana",
      address: solanaWallet.publicKey?.toBase58(),
      chainId: clusterName,
      isTestnet: solanaWallet.connected && clusterName !== "mainnet-beta",
      umi,
      signer: solanaWallet,
    };
  }, [solanaConnection, solanaWallet]);

  return {
    networks: { evm, cosmos, aptos, solana },
  };
};

export default useNetworks;
